/**
 * Creator Discovery Page
 * Search and discover creators/influencers for brand partnerships
 * For Brands to find creators to invite as affiliate partners
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';
import {
  searchCreators,
  getTrendingCreators,
  Creator,
  CreatorSearchFilters,
} from '../../services/creatorDiscovery';
import CreatorCard from '../../components/affiliate/CreatorCard';

export default function CreatorDiscovery() {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [creators, setCreators] = useState<Creator[]>([]);
  const [trendingCreators, setTrendingCreators] = useState<Creator[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<CreatorSearchFilters>({
    sortBy: 'followers',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTrending, setShowTrending] = useState(true);

  useEffect(() => {
    loadBrandProfile();
  }, [user]);

  useEffect(() => {
    if (brandId) {
      loadTrendingCreators();
    }
  }, [brandId]);

  const loadBrandProfile = async () => {
    try {
      if (!user) {
        setError('로그인이 필요합니다.');
        setLoading(false);
        return;
      }

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (sellerError || !sellerProfile) {
        setError('브랜드 프로필을 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      setBrandId(sellerProfile.id);
    } catch (err: any) {
      console.error('Load brand profile error:', err);
      setError('프로필을 불러올 수 없습니다: ' + err.message);
      setLoading(false);
    }
  };

  const loadTrendingCreators = async () => {
    try {
      setLoading(true);
      const { data, error: creatorsError } = await getTrendingCreators(12);

      if (creatorsError) throw creatorsError;

      setTrendingCreators(data || []);
    } catch (err: any) {
      setError('트렌딩 크리에이터를 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() && !filters.minFollowers && !filters.minEngagement) {
      setShowTrending(true);
      return;
    }

    try {
      setLoading(true);
      setShowTrending(false);

      const { data, error: creatorsError } = await searchCreators(
        {
          ...filters,
          searchQuery: searchQuery || undefined,
        },
        50,
        0
      );

      if (creatorsError) throw creatorsError;

      setCreators(data || []);
    } catch (err: any) {
      setError('크리에이터 검색 실패: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (creatorId: string) => {
    if (!brandId) return;
    if (!user) {
      setError('Not authenticated');
      return;
    }

    try {

      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      // Check if already invited
      const { data: existing } = await supabase
        .from('affiliate_partnerships')
        .select('id')
        .eq('brand_id', brandId)
        .eq('creator_id', creatorId)
        .limit(1);

      if (existing && existing.length > 0) {
        setError('이미 초대한 크리에이터입니다.');
        return;
      }

      const { error } = await supabase.from('affiliate_partnerships').insert({
        brand_id: brandId,
        creator_id: creatorId,
        status: 'pending',
        invited_by: profile?.id,
      });

      if (error) throw error;

      setSuccess('크리에이터를 초대했습니다!');
    } catch (err: any) {
      setError('초대 실패: ' + err.message);
    }
  };

  const handleCreatorClick = (creatorId: string) => {
    navigate(`/seller/creator-profile/${creatorId}`);
  };

  const displayCreators = showTrending ? trendingCreators : creators;

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight="bold" sx={{ ...styles.title, mb: 3 }}>
        크리에이터 발견
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="크리에이터 이름 또는 설명으로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: isDarkMode ? '#aaa' : '#666' }} />,
              }}
              sx={styles.input}
            />
          </Grid>

          {/* Sort By */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth sx={styles.input}>
              <InputLabel>정렬</InputLabel>
              <Select
                value={filters.sortBy || 'followers'}
                label="정렬"
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as any })}
              >
                <MenuItem value="followers">팔로워 많은순</MenuItem>
                <MenuItem value="engagement">참여율 높은순</MenuItem>
                <MenuItem value="newest">최근 가입순</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Min Followers Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="최소 팔로워"
              value={filters.minFollowers || ''}
              onChange={(e) =>
                setFilters({ ...filters, minFollowers: e.target.value ? Number(e.target.value) : undefined })
              }
              sx={styles.input}
            />
          </Grid>

          {/* Min Engagement Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="최소 참여율 (%)"
              value={filters.minEngagement || ''}
              onChange={(e) =>
                setFilters({ ...filters, minEngagement: e.target.value ? Number(e.target.value) : undefined })
              }
              sx={styles.input}
            />
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} sm={6} md={1}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ height: 56 }}
            >
              검색
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Trending Badge */}
      {showTrending && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ color: isDarkMode ? '#4caf50' : '#2e7d32' }} />
          <Typography variant="h6" sx={{ ...styles.text, fontWeight: 600 }}>
            트렌딩 크리에이터
          </Typography>
        </Box>
      )}

      {/* Creators Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : displayCreators.length === 0 ? (
        <Paper sx={{ ...styles.paper, p: 8, textAlign: 'center' }}>
          <Typography variant="h6" sx={styles.text}>
            {showTrending ? '트렌딩 크리에이터가 없습니다' : '검색 결과가 없습니다'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={styles.text}>
            {showTrending
              ? '나중에 다시 확인해주세요'
              : '다른 검색어나 필터를 시도해보세요'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {displayCreators.map((creator) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={creator.id}>
              <CreatorCard
                creatorId={creator.id}
                storeName={creator.store_name}
                storeDescription={creator.store_description}
                profileImage={creator.profile_image}
                totalVideos={creator.total_videos}
                totalFollowers={creator.total_followers}
                avgEngagementRate={creator.avg_engagement_rate}
                onClick={handleCreatorClick}
                onInvite={handleInvite}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
